'use client'

import { ClientMatterDetailsMenuItem } from '@repo/ui/ClientMatterDetailsMenuItem'
import { EmailIcon } from '@repo/ui/EmailIcon'
import { formatPhoneNumber } from '@repo/utils/numbers'
import { HouseIcon } from '@repo/ui/HouseIcon'
import { LoadingState } from '@repo/ui/LoadingState'
import { PhoneIcon } from '@repo/ui/PhoneIcon'
import { SmallTitle } from '@repo/ui/SmallTitle'
import { useClientStore } from '@repo/store/useClientStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { UserIcon } from '@repo/ui/UserIcon'
import { useState, useEffect } from 'react'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './MatterDrawerClientTab.module.scss'

export const MatterDrawerClientTab = ({ workspaceDetailCardVisible }: MatterDrawerClientTabProps) => {
  const {
    currentMatter,
    currentClientMatterNo,
    matterDrawerIsOpen,
    relatedMatters,
    setMatterDrawerIsOpen,
    matterDrawerClientNo,
    fetchMatterByClientNo,
    matterDrawerClientMatterNo
  } = useMattersStore()
  const { clients, fetchClient } = useClientStore()
  const { clientNo, clientMatterNo } = currentMatter || {}
  const [client, setClient] = useState<any>(null)
  const { navigateWithClientMatterNo } = useUrlStore()

  const getBillingAddress = () => {
    const { billHouse, billArea, billPostalTown, billCounty, billPostcode } = client || {}
    const billingAddress =
      [billHouse, billArea, billPostalTown, billCounty, billPostcode].filter(Boolean).join(', ') || '-'
    return billingAddress
  }

  useEffect(() => {
    // fetch client details from API
    if (matterDrawerIsOpen && matterDrawerClientNo) {
      fetchClient({ clientNo: matterDrawerClientNo })
    }

    if (!matterDrawerIsOpen && workspaceDetailCardVisible && !matterDrawerClientNo && clientNo) {
      fetchClient({ clientNo: clientNo })
    }
  }, [matterDrawerIsOpen, matterDrawerClientNo, clientNo, workspaceDetailCardVisible])

  useEffect(() => {
    if (matterDrawerClientNo && clients?.[matterDrawerClientNo]) {
      setClient(clients[matterDrawerClientNo])
    }

    if (!matterDrawerClientNo && workspaceDetailCardVisible && clientNo) {
      setClient(clients[clientNo])
    }
  }, [clients, matterDrawerClientNo, workspaceDetailCardVisible, clientNo])

  useEffect(() => {
    fetchMatterByClientNo({ clientNo: matterDrawerClientNo })
  }, [matterDrawerClientNo, fetchMatterByClientNo])

  const goToClientDashboardAndNavigate = async (e: Event) => {
    e.stopPropagation()

    let targetClientMatterNo = currentClientMatterNo

    if (matterDrawerIsOpen) {
      if (matterDrawerClientMatterNo.includes('-')) {
        targetClientMatterNo = matterDrawerClientMatterNo
      } else {
        targetClientMatterNo = relatedMatters[0].clientMatterNo
      }
    }

    setMatterDrawerIsOpen(false)
    navigateWithClientMatterNo({
      route: '/case-management/clients/client-workspace',
      clientMatterNo: targetClientMatterNo,
      appendClientMatterNo: true
    })
  }

  if ((!workspaceDetailCardVisible && !matterDrawerClientNo) || (workspaceDetailCardVisible && !clientNo) || !client) {
    return (
      <MUIStack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', height: '300px' }}
        data-testid="MatterDrawerClientTab"
      >
        <LoadingState />
      </MUIStack>
    )
  }

  const width = document.body.clientWidth < 1200 ? '100%' : 'calc(50% - 12px)'

  return (
    <MUIStack
      data-testid="MatterDrawerClientTab"
      role="tabpanel"
      direction="column"
      gap={6}
      sx={{ padding: '24px', width: '100%' }}
    >
      <MUIStack
        className={styles.clientDetails}
        justifyContent="space-between"
        sx={{ width: 'auto', position: 'relative' }}
        flexWrap="wrap"
        gap={6}
      >
        <ClientMatterDetailsMenuItem
          title="Client workspace"
          label="Click to open"
          variant="large"
          onClick={goToClientDashboardAndNavigate}
          enableCopy={false}
          width={width}
          showHoverIcon={true}
        />

        <ClientMatterDetailsMenuItem
          title="Client no"
          label={matterDrawerClientNo || client.clientNo}
          variant="large"
          width={width}
        />

        <ClientMatterDetailsMenuItem
          title="Client name"
          label={getClientName(client.namedClients[0])}
          variant="large"
          width={width}
        />

        <ClientMatterDetailsMenuItem
          title="Client type"
          label={client.namedClients[0].clientType === 'C' ? 'Company' : 'Individual'}
          variant="large"
          width={width}
        />

        <ClientMatterDetailsMenuItem
          title="Billing addressee"
          label={client?.billAddressee || '-'}
          variant="large"
          width={width}
        />

        <ClientMatterDetailsMenuItem
          title="Billing address"
          label={getBillingAddress()}
          variant="large"
          width={width}
          noWrap={false}
        />
      </MUIStack>

      <SmallTitle title="Named clients" />
      {renderClientListElements(client.namedClients)}

      {client.clientPhones?.length ? (
        <>
          <SmallTitle title="Phone numbers" sx={{ mt: 6 }} />
          <MUIStack direction="row" justifyContent="space-between" sx={{ width: '100%' }} flexWrap="wrap" gap={6}>
            {renderPhoneNumberElements(client.clientPhones)}
          </MUIStack>
        </>
      ) : null}
    </MUIStack>
  )
}

const getClientName = (namedClient: any) => {
  if (!namedClient) return null
  if (namedClient.companyName) return namedClient.companyName

  const { title, forename, surname } = namedClient
  let clientName = `${title ? `${title} ` : ''}${forename ? `${forename} ` : ''}${surname}`
  return clientName
}

const getClientAddress = (namedClient: any) => {
  return namedClient
    ? [namedClient.house, namedClient.area, namedClient.postalTown, namedClient.county, namedClient.postcode]
        .filter(Boolean)
        .join(', ')
    : null
}

const renderClientListElements = (namedClients: any) => {
  return namedClients.map((namedClient: any, index: number) => {
    const clientName = getClientName(namedClient)
    const address = getClientAddress(namedClient)

    return (
      <div
        key={`client-${clientName}-${index}`}
        style={{
          width: '100%',
          borderRadius: '6px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          padding: '8px 12px'
        }}
      >
        {clientName ? (
          <ClientMatterDetailsMenuItem
            sx={{ m: 0 }}
            label={clientName}
            icon={<UserIcon sx={{ fontSize: '24px', m: '6px 0' }} />}
          />
        ) : null}
        {namedClient.emailAddress ? (
          <ClientMatterDetailsMenuItem
            sx={{ m: 0 }}
            label={namedClient.emailAddress}
            icon={<EmailIcon sx={{ fontSize: '24px', m: '6px 0' }} />}
          />
        ) : null}
        {address ? (
          <ClientMatterDetailsMenuItem
            sx={{ m: 0 }}
            label={address}
            noWrap={false}
            icon={<HouseIcon sx={{ fontSize: '24px', m: '6px 0' }} />}
          />
        ) : null}
      </div>
    )
  })
}

const renderPhoneNumberElements = (clientPhones: any) => {
  return clientPhones?.map((phone: any, index: number) => {
    if (!phone?.phoneNumber && !phone?.description) return null

    return (
      <div key={`phone-${phone.phoneNumber}`} style={{ width: '100%' }}>
        <ClientMatterDetailsMenuItem
          key={`${phone.phoneNumber}-${index}`}
          label={formatPhoneNumber(phone.phoneNumber) || formatPhoneNumber(phone.description)}
          secondaryLabel={phone.phoneNumber ? phone.description : null}
          icon={<PhoneIcon sx={{ fontSize: '24px', m: '8px 0' }} />}
        />
      </div>
    )
  })
}

type MatterDrawerClientTabProps = {
  workspaceDetailCardVisible: boolean
}

MatterDrawerClientTab.propTypes = {
  workspaceDetailCardVisible: PropTypes.bool
}
